import { useState } from "react";
import { isMobile } from "react-device-detect";
import toast from 'react-hot-toast';
import TrackingAPI from 'api/TrackingAPI';
import CommonHelper from 'helpers/CommonHelper';
import { isNil } from "ramda";
import { useDispatch } from 'redux-react-hook';
import { analyticsService } from 'services/analyticsService';
import { ITokenPackage, useCreatePaymentSessionMutation, useCreateSilentTokenPurchaseMutation } from "services/api/tokenApi";
import { refetchCurrentUser } from "store/DEPRECATEDusers/asyncAction";
import styled from "styled-components";
import { getErrorMessage } from "utils/error";
import useQueryParams from "utils/useQueryParams";

import PlanCard from "./components/PlanCard";

interface IChoosePlanSectionProps {
  packages: ITokenPackage[];
  selectedPlan: ITokenPackage | null;
  setSelectedPlan: (plan: ITokenPackage | null) => void;
  useSavedCard: boolean;
  onClose: () => void;
}

const ANALYTIC_EVENT_NAME = 'buy_tokens_click';

const ChoosePlanSection: React.FC<IChoosePlanSectionProps> = (props) => {
  const { packages, selectedPlan, setSelectedPlan, useSavedCard, onClose } = props;
  const [createPaymentSession] = useCreatePaymentSessionMutation();
  const [createSilentTokenPurchase] = useCreateSilentTokenPurchaseMutation();
  const [loadingPlanId, setLoadingPlanId] = useState<string | null>(null);
  const { updateQueryParams } = useQueryParams();
  const dispatch = useDispatch();

  const trackAnalytics = (externalId: string | undefined) => {
    if (!externalId) return;
    TrackingAPI.trackEvent(ANALYTIC_EVENT_NAME, { external_id: externalId });
    analyticsService.trackGoogleEvent(ANALYTIC_EVENT_NAME, { external_id: externalId });
    CommonHelper.yandexTrackEvent(ANALYTIC_EVENT_NAME, { external_id: externalId });
  };

  const handlePlanSelect = async (plan: ITokenPackage | null) => {
    if (!isNil(loadingPlanId)) return;

    setSelectedPlan(plan);
    if (!plan) {
      return;
    }

    try {
      setLoadingPlanId(plan.externalId || null);

      if (useSavedCard) {
        const { success } = await createSilentTokenPurchase({ external_id: plan.externalId as string }).unwrap();
        if (success) {
          trackAnalytics(plan.externalId);
          dispatch(refetchCurrentUser());
          updateQueryParams({ paid: 'true', paidType: 'token' });
          onClose();
          toast.success('Tokens purchased successfully');
        }
      } else {
        const { paymentGatewayUrl } = await createPaymentSession({ external_id: plan.externalId as string }).unwrap();
        if (paymentGatewayUrl) {
          trackAnalytics(plan.externalId);
          window.location.href = paymentGatewayUrl;
        }
      }
    } catch (error) {
      setSelectedPlan(null);
      setLoadingPlanId(null);
      toast.error(getErrorMessage(error));
      throw error;
    }
  };

  const sortedPackages = [...packages].sort((a, b) => (b.sortOrder ?? 0) - (a.sortOrder ?? 0));

  return (
    <Root isMobile={isMobile}>
      <CardsContainer>
        {sortedPackages.map(plan => (
          <PlanCard
            key={plan.externalId}
            selected={plan.externalId === selectedPlan?.externalId}
            onSelect={handlePlanSelect}
            plan={plan}
            isLoading={loadingPlanId === plan.externalId}
          />
        ))}
      </CardsContainer>
    </Root>
  );
};

export default ChoosePlanSection;

const CardsContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 16px;
  width: 100%;

  @media (max-width: 376px) {
    gap: 13px;
  }
`;

const Root = styled.div<{ isMobile: boolean }>`
  width: 100%;
`;
